import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { IUser, UserRole } from "@api/schema"
import { IRequestState } from "@redux/helper/state"
import { AppState } from "@redux/reducer"
import { selectCurrentUser, selectIsAuthenticated } from "@redux/reducer/auth"
import { loadCurrentUserAction, selectLoadingCurrentUserUsecaseState } from "@redux/usecases/userAccount"


/**
 * structure of what the useCurrentUser hook returns
 */
export interface IUseCurrentUserRequestData {
  currentUser: IUser
  isAdmin: boolean
  isAuthenticated: boolean
  isProcessManager: boolean
  roles: UserRole[]
  userRequest: IRequestState
}

/**
 * Hook to use the current user.
 * Takes it from the state or loads it
 * if the user is authenticated but there is no currentUser object.
 *
 * @param loadIfNotPresent Should the hook load the current user if it is not in state?
 * To avoid unnecessary loads, e.g. if no project slug is given the user who wants to access a page is not important.
 * default: true
 * @returns data regarding the current user
 */
export const useCurrentUser = (loadIfNotPresent = true): IUseCurrentUserRequestData => {
  const dispatch = useDispatch()

  const isAuthenticated = useSelector((state: AppState) => selectIsAuthenticated(state))
  const currentUser = useSelector((state: AppState) => selectCurrentUser(state))
  const userRequest = useSelector((state: AppState) => selectLoadingCurrentUserUsecaseState(state))
  const roles = useSelector((state: AppState) => state.auth.roles)
  const isProcessManager = roles?.includes(UserRole.ProcessManager)
  const isAdmin = roles?.includes(UserRole.Admin)

  useEffect(() => {
    if (isAuthenticated && !currentUser && loadIfNotPresent && (!userRequest || (userRequest && !userRequest.isLoading))) {
      dispatch(loadCurrentUserAction())
    }
  }, [isAuthenticated])

  return { currentUser, isAdmin, isAuthenticated, isProcessManager, roles, userRequest }
}